<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useDecorationStore } from "#/store/decoration";
import { useNutritionBasisStore } from "#/store/nutritionBasis";

const props = defineProps<{ decoration?: string }>();

const { t } = useTranslation();
const basis = useNutritionBasisStore();
const decor = useDecorationStore().screen(props.decoration);

const root = ref<HTMLElement | null>(null);
const offset = ref(0);
const color = ref("");
const marginBottom = ref<number | null>(null);

let frame = 0;
let baseMargin: number | null = null;
let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;

const firstText = (cell: Element): Text | null => {
	const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => (node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
	});
	return walker.nextNode() as Text | null;
};

const measure = () => {
	frame = 0;
	const el = root.value;
	const cell = el?.nextElementSibling;
	if (!el || !cell) return;
	const text = firstText(cell);
	if (!text) return;
	const range = document.createRange();
	range.selectNodeContents(text);
	const rect = range.getBoundingClientRect();
	if (!rect.width && !rect.height) return;
	const box = el.getBoundingClientRect();
	const scale = el.offsetWidth ? box.width / el.offsetWidth : 1;
	offset.value = Math.max(0, Math.round((rect.left - box.left) / (scale || 1)));
	color.value = text.parentElement ? getComputedStyle(text.parentElement).color : "";
	baseMargin ??= Number.parseFloat(getComputedStyle(el).marginBottom) || 0;
	const rowGap = el.parentElement ? Number.parseFloat(getComputedStyle(el.parentElement).rowGap) || 0 : 0;
	marginBottom.value = baseMargin - rowGap / 2;
};

const schedule = () => {
	if (!frame) frame = requestAnimationFrame(measure);
};

onMounted(() => {
	measure();
	const el = root.value;
	const cell = el?.nextElementSibling;
	if (!el || !cell || !el.parentElement) return;
	resizeObserver = new ResizeObserver(schedule);
	resizeObserver.observe(el.parentElement);
	resizeObserver.observe(cell);
	mutationObserver = new MutationObserver(schedule);
	mutationObserver.observe(cell, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["style", "class"] });
	document.fonts?.addEventListener("loadingdone", schedule);
	void document.fonts?.ready.then(schedule);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	mutationObserver?.disconnect();
	document.fonts?.removeEventListener("loadingdone", schedule);
	if (frame) cancelAnimationFrame(frame);
});
</script>

<template>
	<div
		ref="root"
		class="nutrition_basis"
		data-el="nutrition_basis"
		:style="{ '--nutrition-basis-offset': `${offset}px`, color: color || undefined, marginBottom: marginBottom === null ? undefined : `${marginBottom}px` }"
		v-bind="decor.bind('nutrition_basis')"
		@click.stop.prevent
	>
		<button
			type="button"
			class="nutrition_basis_option"
			data-el="nutrition_basis_option"
			:class="{ nutrition_basis_active: !basis.isPer100g }"
			:aria-pressed="!basis.isPer100g"
			v-bind="decor.bind('nutrition_basis_option', { active: !basis.isPer100g })"
			:data-label="t(`card.nutritional.whole-dish`)"
			@click="basis.isPer100g = false"
		>
			{{ t(`card.nutritional.whole-dish`) }}
		</button>
		<span>•</span>
		<button
			type="button"
			class="nutrition_basis_option"
			data-el="nutrition_basis_option"
			:class="{ nutrition_basis_active: basis.isPer100g }"
			:aria-pressed="basis.isPer100g"
			v-bind="decor.bind('nutrition_basis_option', { active: basis.isPer100g })"
			:data-label="t(`card.nutritional.per-100g`)"
			@click="basis.isPer100g = true"
		>
			{{ t(`card.nutritional.per-100g`) }}
		</button>
	</div>
</template>

<style lang="scss">
  @use './nutritionBasis.scss';
</style>
